import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import * as api from "./adminApi";
import {
  AdminShell,
  Area,
  Btn,
  Card,
  Field,
  InlineForm,
  StatusBadge,
  Text,
  useAsync,
} from "./components";

function truncate(text: string, max: number): string {
  return text.length > max ? `${text.slice(0, max)}…` : text;
}

export function LearningItemPage() {
  const { id = "" } = useParams();
  const { data: li, error, reload } = useAsync(() => api.getLearningItem(id), [id]);

  const [title, setTitle] = useState("");
  const [downloadAllowed, setDownloadAllowed] = useState(false);
  const [externalUrl, setExternalUrl] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploadMsg, setUploadMsg] = useState<string | null>(null);
  const [uploadErr, setUploadErr] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (li) {
      setTitle(li.title);
      setDownloadAllowed(li.downloadAllowed);
      setExternalUrl(li.externalUrl ?? "");
    }
  }, [li]);

  if (error)
    return (
      <AdminShell crumbs={[{ label: "Admin", to: "/admin" }]} title="Item">
        {error}
      </AdminShell>
    );
  if (!li)
    return (
      <AdminShell crumbs={[{ label: "Admin", to: "/admin" }]} title="Loading…">
        {null}
      </AdminShell>
    );

  async function upload() {
    const file = fileRef.current?.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadErr(null);
    setUploadMsg(null);
    try {
      const r = await api.uploadHtmlPackage(li!.id, file);
      setUploadMsg(
        r.fileCount === 1
          ? `Uploaded — single HTML file (${r.entryFile})`
          : `Uploaded — ${r.fileCount} files, entry ${r.entryFile}`,
      );
      if (fileRef.current) fileRef.current.value = "";
      reload();
    } catch (e) {
      setUploadErr(e instanceof Error ? e.message : String(e));
    } finally {
      setUploading(false);
    }
  }

  async function uploadResourceFile() {
    const file = fileRef.current?.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadErr(null);
    setUploadMsg(null);
    try {
      const r = await api.uploadResource(li!.id, file);
      setUploadMsg(`Uploaded — ${r.filename}`);
      if (fileRef.current) fileRef.current.value = "";
      reload();
    } catch (e) {
      setUploadErr(e instanceof Error ? e.message : String(e));
    } finally {
      setUploading(false);
    }
  }

  return (
    <AdminShell
      crumbs={[
        { label: "Admin", to: "/admin" },
        { label: li.module.title, to: `/admin/modules/${li.module.id}` },
        { label: truncate(li.title, 60) },
      ]}
      title={truncate(li.title, 100)}
    >
      <Card>
        <div
          style={{ display: "flex", gap: "0.6rem", alignItems: "center", marginBottom: "0.8rem" }}
        >
          <StatusBadge status={li.status} />
          <span style={{ color: "#888", fontSize: "0.85rem" }}>
            {li.type.replace(/_/g, " ").toLowerCase()}
          </span>
          <Btn
            kind="ghost"
            onClick={async () => {
              await api.setPublished("learningItem", li.id, li.status !== "PUBLISHED");
              reload();
            }}
          >
            {li.status === "PUBLISHED" ? "Unpublish" : "Publish"}
          </Btn>
          {li.module.status !== "PUBLISHED" && (
            <span style={{ color: "#b00020", fontSize: "0.8rem" }}>
              Module must be published first
            </span>
          )}
        </div>
        <InlineForm
          submitLabel="Save"
          onSubmit={async () => {
            await api.updateLearningItem(li.id, {
              title,
              downloadAllowed,
              ...(li.type === "EXTERNAL_LINK" ? { externalUrl } : {}),
            });
            reload();
          }}
        >
          <Field label="Title / objective (up to 1000 characters, ~4-5 lines)">
            <Area value={title} onChange={(e) => setTitle(e.target.value)} rows={4} required />
          </Field>
          {li.type === "EXTERNAL_LINK" && (
            <Field label="URL">
              <Text
                type="url"
                value={externalUrl}
                onChange={(e) => setExternalUrl(e.target.value)}
              />
            </Field>
          )}
          <label style={{ display: "block", marginBottom: "0.7rem", fontSize: "0.9rem" }}>
            <input
              type="checkbox"
              checked={downloadAllowed}
              onChange={(e) => setDownloadAllowed(e.target.checked)}
            />{" "}
            Learners may download this item
          </label>
        </InlineForm>
      </Card>

      {li.type === "HTML_PACKAGE" && (
        <Card>
          <strong>HTML content</strong>
          {li.htmlPackage ? (
            <p style={{ color: "#0a7d33", fontSize: "0.9rem" }}>
              Uploaded · entry <code>{li.htmlPackage.entryFile}</code>
            </p>
          ) : (
            <p style={{ color: "#b00020", fontSize: "0.9rem" }}>Nothing uploaded yet.</p>
          )}
          <p style={{ color: "#666", fontSize: "0.85rem" }}>
            Upload a single self-contained <code>.html</code> file, or a <code>.zip</code> with{" "}
            <code>index.html</code> at its root (for content that has separate CSS / JS / images).
            Replacing overwrites the current content. The page must call{" "}
            <code>bridge.reportCompleted()</code> to finish the module.
          </p>
          <input ref={fileRef} type="file" accept=".zip,.html,.htm,application/zip,text/html" />
          <div style={{ marginTop: "0.6rem" }}>
            <Btn onClick={upload} disabled={uploading}>
              {uploading ? "Uploading…" : "Upload"}
            </Btn>
          </div>
          {uploadMsg && <p style={{ color: "#0a7d33", fontSize: "0.85rem" }}>{uploadMsg}</p>}
          {uploadErr && <p style={{ color: "#b00020", fontSize: "0.85rem" }}>{uploadErr}</p>}
        </Card>
      )}

      {li.type === "DOWNLOADABLE_RESOURCE" && (
        <Card>
          <strong>File</strong>
          {li.resources.length > 0 ? (
            <p style={{ color: "#0a7d33", fontSize: "0.9rem" }}>
              Uploaded · <code>{li.resources[0]!.filename}</code>
            </p>
          ) : (
            <p style={{ color: "#b00020", fontSize: "0.9rem" }}>Nothing uploaded yet.</p>
          )}
          <p style={{ color: "#666", fontSize: "0.85rem" }}>
            Upload the file learners will download (PDF, slides, etc.). Replacing overwrites the
            current file. Only downloadable if "Learners may download this item" is checked above —
            the download link always goes through a short-lived, per-request authorised link, never
            a public URL.
          </p>
          <input ref={fileRef} type="file" />
          <div style={{ marginTop: "0.6rem" }}>
            <Btn onClick={uploadResourceFile} disabled={uploading}>
              {uploading ? "Uploading…" : "Upload"}
            </Btn>
          </div>
          {uploadMsg && <p style={{ color: "#0a7d33", fontSize: "0.85rem" }}>{uploadMsg}</p>}
          {uploadErr && <p style={{ color: "#b00020", fontSize: "0.85rem" }}>{uploadErr}</p>}
        </Card>
      )}
    </AdminShell>
  );
}
